import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2, Minus, Plus, RefreshCw, Shield } from 'lucide-react';
import { useSharedPermissions } from '../contexts/PermissionsContext';
import { authConfig } from '../auth/config';
import { permissionKey, useAuth0AdministrationApi, type Auth0Permission, type Auth0Role } from '../services/auth0AdministrationService';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import './Auth0PermissionsMatrix.css';

type Column = { keys: Set<string>; status: 'loading' | 'ready' | 'saving' | 'failed'; error?: string };
type Group = { key: string; api: string; label: string; permissions: Auth0Permission[] };
const errorMessage = (error: unknown) => error instanceof Error ? error.message : 'Unable to complete the Auth0 request.';

export function Auth0PermissionsMatrix() {
  const { isAdmin } = useSharedPermissions();
  const api = useAuth0AdministrationApi();
  const [roles, setRoles] = useState<Auth0Role[]>([]);
  const [permissions, setPermissions] = useState<Auth0Permission[]>([]);
  const [selectedApi, setSelectedApi] = useState(authConfig.audience);
  const [search, setSearch] = useState('');
  const apiSelectId = useId();
  const [columns, setColumns] = useState<Record<string, Column>>({});
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const pendingRoles = useRef(new Set<string>());
  const readQueue = useRef({ active: 0, waiting: [] as (() => void)[] });
  const readRole = useCallback(async (id: string) => {
    const queue = readQueue.current;
    if (queue.active < 3) queue.active++;
    else await new Promise<void>(resolve => queue.waiting.push(resolve));
    try { return await api.role(id); }
    finally {
      const next = queue.waiting.shift();
      if (next) next(); else queue.active--;
    }
  }, [api]);

  const load = useCallback(async () => {
    if (!isAdmin || pendingRoles.current.size > 0) return;
    const version = ++generation.current;
    setLoading(true); setError(''); setRoles([]); setColumns({});
    try {
      const capability = await api.capabilities();
      if (version !== generation.current) return;
      if (!capability.configured) { setError(capability.message || 'Auth0 administration is not configured.'); return; }
      const [nextRoles, nextPermissions] = await Promise.all([api.roles(), api.permissions()]);
      if (version !== generation.current) return;
      nextRoles.sort((a, b) => a.name.localeCompare(b.name));
      setRoles(nextRoles); setPermissions(nextPermissions);
      setSelectedApi(current => nextPermissions.some(p => p.resourceServerIdentifier === current) ? current : authConfig.audience);
      setColumns(Object.fromEntries(nextRoles.map(role => [role.id, { keys: new Set<string>(), status: 'loading' }])));
      let index = 0;
      const worker = async () => {
        while (index < nextRoles.length && version === generation.current) {
          const role = nextRoles[index++];
          try {
            const details = await readRole(role.id);
            if (version === generation.current) setColumns(current => ({ ...current, [role.id]: { keys: new Set(details.permissions.map(permissionKey)), status: 'ready' } }));
          } catch (e) {
            if (version === generation.current) setColumns(current => ({ ...current, [role.id]: { keys: new Set<string>(), status: 'failed', error: errorMessage(e) } }));
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(3, nextRoles.length) }, worker));
    } catch (e) { if (version === generation.current) setError(errorMessage(e)); }
    finally { if (version === generation.current) setLoading(false); }
  }, [api, isAdmin, readRole]);

  useEffect(() => {
    const lifecycle = generation;
    void load();
    return () => { lifecycle.current++; };
  }, [load]);

  const reloadRole = async (id: string) => {
    if (pendingRoles.current.has(id)) return;
    pendingRoles.current.add(id);
    const version = generation.current;
    setColumns(current => ({ ...current, [id]: { ...current[id], status: 'loading', error: undefined } }));
    try {
      const details = await readRole(id);
      if (version === generation.current) setColumns(current => ({ ...current, [id]: { keys: new Set(details.permissions.map(permissionKey)), status: 'ready' } }));
    } catch (e) {
      if (version === generation.current) setColumns(current => ({ ...current, [id]: { ...current[id], status: 'failed', error: errorMessage(e) } }));
    } finally { pendingRoles.current.delete(id); }
  };

  const change = async (role: Auth0Role, permission: Auth0Permission, assigned: boolean) => {
    if (columns[role.id]?.status !== 'ready' || pendingRoles.current.has(role.id)) return;
    pendingRoles.current.add(role.id);
    const version = generation.current;
    setColumns(current => ({ ...current, [role.id]: { ...current[role.id], status: 'saving', error: undefined } }));
    let saveError = '';
    try { await api.changeRolePermission(role.id, permission, assigned); }
    catch (e) { saveError = `${errorMessage(e)} The change may have completed.`; }
    try {
      const details = await readRole(role.id);
      if (version === generation.current) setColumns(current => ({ ...current, [role.id]: {
        keys: new Set(details.permissions.map(permissionKey)), status: 'ready', error: saveError ? `${saveError} Actual assignments have been reloaded.` : undefined,
      } }));
    } catch (e) {
      if (version === generation.current) setColumns(current => ({ ...current, [role.id]: {
        ...current[role.id], status: 'failed', error: `${saveError || 'The save outcome could not be confirmed.'} ${errorMessage(e)} Retry to reload this role before editing.`,
      } }));
    } finally { pendingRoles.current.delete(role.id); }
  };

  const apiOptions = useMemo(() => {
    const options = new Map<string, string>([[authConfig.audience, authConfig.audience === 'https://lu-commercial-api' ? 'lu-commercial-api' : authConfig.audience]]);
    for (const permission of permissions) options.set(permission.resourceServerIdentifier, permission.apiName || permission.resourceServerIdentifier);
    return [...options].sort((a, b) => a[1].localeCompare(b[1]));
  }, [permissions]);
  const visiblePermissions = useMemo(() => permissions.filter(p => p.resourceServerIdentifier === selectedApi), [permissions, selectedApi]);
  const filteredPermissions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return visiblePermissions.filter(permission => !query || permission.permissionName.toLowerCase().includes(query) || permission.description.toLowerCase().includes(query));
  }, [visiblePermissions, search]);
  const groups = useMemo(() => {
    const map = new Map<string, Group>();
    for (const permission of filteredPermissions) {
      const colon = permission.permissionName.indexOf(':');
      const label = colon > 0 ? permission.permissionName.slice(0, colon + 1) : 'Other';
      const key = JSON.stringify([permission.resourceServerIdentifier, label]);
      if (!map.has(key)) map.set(key, { key, api: permission.apiName || permission.resourceServerIdentifier, label, permissions: [] });
      map.get(key)!.permissions.push(permission);
    }
    return [...map.values()].sort((a, b) => a.api.localeCompare(b.api) || a.label.localeCompare(b.label)).map(group => ({
      ...group, permissions: group.permissions.sort((a, b) => a.permissionName.localeCompare(b.permissionName)),
    }));
  }, [filteredPermissions]);
  const allCollapsed = groups.length > 0 && groups.every(group => collapsed.has(group.key));
  const active = Object.values(columns).some(c => c.status === 'loading' || c.status === 'saving');
  const loaded = Object.values(columns).filter(c => c.status !== 'loading').length;
  const headerHeight = Math.max(160, ...roles.map(role => role.name.length * 9 + 50));

  if (!isAdmin) return <Alert><AlertDescription>Only administrators can manage role permissions.</AlertDescription></Alert>;
  return <section className="auth0-matrix-card" data-analytics-private="true" aria-label="Auth0 permissions matrix">
    <div className="auth0-matrix-toolbar">
      <div><h2><Shield size={18} aria-hidden="true" />Auth0 Core RBAC Permissions Matrix</h2>
        <p>Click a checkbox to assign or remove a role permission. Changes are saved in Auth0.</p></div>
      <Button variant="outline" onClick={() => void load()} disabled={loading || active}><RefreshCw className="size-4 mr-2" />Refresh</Button>
    </div>
    <div className="auth0-matrix-api-filter">
      <label htmlFor={apiSelectId}>Auth0 API</label>
      <select id={apiSelectId} value={selectedApi} onChange={event => { setSelectedApi(event.target.value); setSearch(''); }}
        disabled={loading || Object.values(columns).some(column => column.status === 'saving')}>
        {apiOptions.map(([identifier, name]) => <option key={identifier} value={identifier}>{name} ({identifier})</option>)}
      </select>
      <div className="auth0-matrix-permission-controls">
        <Button variant="outline" size="icon" aria-label={allCollapsed ? 'Expand all permission groups' : 'Collapse all permission groups'}
          title={allCollapsed ? 'Expand all permission groups' : 'Collapse all permission groups'} disabled={loading || groups.length === 0 || roles.length === 0}
          onClick={() => setCollapsed(current => {
            const next = new Set(current);
            for (const group of groups) { if (allCollapsed) next.delete(group.key); else next.add(group.key); }
            return next;
          })}>
          {allCollapsed ? <Plus size={16} aria-hidden="true" /> : <Minus size={16} aria-hidden="true" />}
        </Button>
        <Input type="search" className="auth0-matrix-permission-search" aria-label="Search permissions" placeholder="Search permissions…"
          value={search} onChange={event => {
            setSearch(event.target.value);
            if (event.target.value.trim()) setCollapsed(current => new Set([...current].filter(key => JSON.parse(key)[0] !== selectedApi)));
          }} />
      </div>
    </div>
    <p className="auth0-matrix-note">Updated access applies to newly issued tokens; existing sessions may need to sign in again.</p>
    {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription><Button variant="outline" onClick={() => void load()}>Retry</Button></Alert>}
    {loading && <p role="status">{roles.length > 0 ? `Loading role assignments: ${loaded} of ${roles.length}` : 'Loading Auth0 matrix…'}</p>}
    {roles.filter(role => columns[role.id]?.error).map(role => <Alert key={role.id} variant="destructive"><AlertDescription>
      <strong>{role.name}:</strong> {columns[role.id].error}
    </AlertDescription><Button variant="outline" disabled={columns[role.id].status === 'loading' || columns[role.id].status === 'saving'} onClick={() => void reloadRole(role.id)}>Retry {role.name}</Button></Alert>)}
    {!loading && !error && roles.length === 0 && <p>No roles exist yet. Create a role in the Roles tab.</p>}
    {!loading && !error && permissions.length === 0 && <p>No API permissions are defined.</p>}
    {!loading && !error && permissions.length > 0 && visiblePermissions.length === 0 && <p>No permissions are defined for the selected API.</p>}
    {!loading && !error && visiblePermissions.length > 0 && filteredPermissions.length === 0 && <p role="status">No permissions match your search.</p>}
    {roles.length > 0 && filteredPermissions.length > 0 && <div className="auth0-matrix-scroll" tabIndex={0} aria-label="Scrollable permission and role assignments">
      <table className="auth0-matrix-table" style={{ width: `calc(var(--permission-width) + ${roles.length * 56}px)` }}>
        <colgroup><col className="auth0-matrix-permission-col" />{roles.map(role => <col key={role.id} style={{ width: 56 }} />)}</colgroup>
        <thead><tr style={{ height: headerHeight }}><th scope="col" className="auth0-matrix-corner">API Scope Permission</th>
          {roles.map(role => <th scope="col" key={role.id} className="auth0-matrix-role-header" aria-label={role.name}>
            <Tooltip><TooltipTrigger asChild><span className="auth0-matrix-role-name" tabIndex={0}>{role.name}</span></TooltipTrigger><TooltipContent>{role.name}{role.description && ` — ${role.description}`}</TooltipContent></Tooltip>
            {columns[role.id]?.status !== 'ready' && <span className="auth0-matrix-role-status" role="status" aria-label={`${role.name}: ${columns[role.id]?.status}`}>
              {columns[role.id]?.status === 'failed' ? '!' : <Loader2 size={14} className="animate-spin" aria-hidden="true" />}
            </span>}
          </th>)}
        </tr></thead>
        <tbody>{groups.map((group, groupIndex) => <Fragment key={group.key}>
          <tr className="auth0-matrix-group"><th className="auth0-matrix-label" scope="row">
            <button className="auth0-matrix-group-toggle" aria-expanded={!collapsed.has(group.key)} aria-controls={`matrix-group-${groupIndex}`} onClick={() => setCollapsed(current => {
              const next = new Set(current); if (next.has(group.key)) next.delete(group.key); else next.add(group.key); return next;
            })}>
              {collapsed.has(group.key) ? <ChevronRight size={16} /> : <ChevronDown size={16} />}<span>{group.label}<small>{group.api}</small></span><span className="auth0-matrix-count">{group.permissions.length}</span>
            </button>
          </th>{roles.map(role => <td key={role.id} />)}</tr>
          {group.permissions.map((permission, index) => <tr key={permissionKey(permission)} hidden={collapsed.has(group.key)} id={index === 0 ? `matrix-group-${groupIndex}` : undefined}>
            <th scope="row" className="auth0-matrix-label" title={`${permission.resourceServerIdentifier} — ${permission.description}`}>{permission.permissionName}</th>
            {roles.map(role => <td key={role.id} className="auth0-matrix-cell">
              <Checkbox checked={columns[role.id]?.keys.has(permissionKey(permission)) ?? false}
                disabled={columns[role.id]?.status !== 'ready'}
                aria-label={`${role.name}: ${permission.permissionName} (${permission.resourceServerIdentifier})`}
                onCheckedChange={checked => void change(role, permission, checked === true)} />
            </td>)}
          </tr>)}
        </Fragment>)}</tbody>
      </table>
    </div>}
  </section>;
}
